import { DEFAULT_THEME_ID, getSiteTheme } from './siteThemes';

export const DEFAULT_SITE_COLOR = '#c71a1a';
export const DEFAULT_SURFACE_COLOR = DEFAULT_SITE_COLOR;
export const SITE_THEME_STORAGE_KEY = 'app:site-theme-v1';
export const SITE_THEME_ID_STORAGE_KEY = 'app:site-theme-id-v1';
export const SITE_THEME_ATTRIBUTE = 'data-site-theme';

const normalizeHexColor = (value, fallback) => {
  const color = String(value || '').trim();
  return /^#[0-9a-f]{6}$/i.test(color) ? color.toLowerCase() : fallback;
};

export const normalizeSiteColor = (value) => (
  normalizeHexColor(value, DEFAULT_SITE_COLOR)
);

export const normalizeSurfaceColor = (value) => (
  normalizeHexColor(value, DEFAULT_SURFACE_COLOR)
);

// Bilinmeyen bir şema adı gelirse (eski kayıt, elle düzenlenmiş DB) varsayılana
// düşülür; site renksiz kalmaz.
export const normalizeThemeId = (value) => (
  getSiteTheme(String(value || '').trim().toLowerCase()).id
);

const mix = (rgb, target, amount) => rgb.map((channel, index) => (
  Math.round(channel + ((target[index] - channel) * amount))
));

const toHex = (rgb) => `#${rgb.map((channel) => (
  channel.toString(16).padStart(2, '0')
)).join('')}`;

const toRgb = (color) => [
  Number.parseInt(color.slice(1, 3), 16),
  Number.parseInt(color.slice(3, 5), 16),
  Number.parseInt(color.slice(5, 7), 16),
];

// WCAG göreli parlaklığı: kavramsal parlaklık formülünden farklı olarak koyu
// zeminde bir rengin gerçekten okunup okunmadığını söyler.
const relativeLuminance = (rgb) => {
  const [r, g, b] = rgb.map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
};

const contrastRatio = (rgbA, rgbB) => {
  const a = relativeLuminance(rgbA);
  const b = relativeLuminance(rgbB);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
};

// Vurgu renginin üzerine yazılacak metin: siyah mı beyaz mı daha okunur?
const readableTextOn = (rgb) => (
  contrastRatio(rgb, [9, 9, 13]) >= contrastRatio(rgb, [255, 255, 255])
    ? '#09090d'
    : '#ffffff'
);

// Admin yüzeyleri koyudur; site için seçilen renk çok koyuysa (lacivert, koyu
// mor) panelde yazı ve kenarlık olarak kullanıldığında kaybolur. Bu yüzden
// vurgu, koyu zemine karşı asgari 3:1 kontrastı yakalayana dek açılır.
const ADMIN_SURFACE_RGB = [16, 16, 22];

const lightenUntilReadable = (rgb) => {
  let current = rgb;
  for (let step = 0; step < 24 && contrastRatio(current, ADMIN_SURFACE_RGB) < 3; step += 1) {
    current = mix(current, [255, 255, 255], 0.08);
  }
  return current;
};

export const DEFAULT_ADMIN_ACCENT = '#25ab6c';

// Yönetim panelinin vurgu tokenları site renklerinden türetilir; böylece
// panelden seçilen tema admin arayüzünde de karşılığını bulur.
export const buildAdminTheme = (primaryValue, surfaceValue) => {
  const accent = normalizeHexColor(primaryValue, DEFAULT_ADMIN_ACCENT);
  const rgb = lightenUntilReadable(toRgb(accent));
  // Üst alan rengi boşsa site tarafındaki gibi ana renge düşülür; tek renk
  // seçimi panelin arka plan ışımasını da değiştirir.
  const tintRgb = toRgb(normalizeHexColor(surfaceValue, toHex(rgb)));

  return {
    '--admin-theme-accent': toHex(rgb),
    '--admin-theme-accent-rgb': rgb.join(', '),
    '--admin-theme-accent-hover': toHex(mix(rgb, [255, 255, 255], 0.22)),
    '--admin-theme-accent-dark': toHex(mix(rgb, [0, 0, 0], 0.27)),
    '--admin-theme-on-accent': readableTextOn(rgb),
    // Arka plandaki geniş ışıma; ham renk fazla parlak kaldığı için karartılır.
    '--admin-theme-tint-rgb': mix(tintRgb, [0, 0, 0], 0.45).join(', '),
  };
};

export const applyAdminTheme = (
  primaryValue,
  surfaceValue,
  root = typeof document !== 'undefined' ? document.documentElement : null,
) => {
  const variables = buildAdminTheme(primaryValue, surfaceValue);
  if (!root) return variables;
  Object.entries(variables).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
  return variables;
};

export const buildSiteTheme = (primaryValue, surfaceValue, themeId = DEFAULT_THEME_ID) => {
  const theme = getSiteTheme(normalizeThemeId(themeId));
  // Renkleri kimliği olan şemalarda (Vega) ayar kaydındaki renk **hiç
  // okunmaz**: panel o alanları çizmez ama kayıtta önceki şemadan kalan bir
  // renk durabilir ve okunsaydı siyah-altın şema başka bir vurguyla açılırdı.
  const fixed = Boolean(theme.fixedColors);
  // Renk alanı boşsa şemanın kendi vurgusu kullanılır; yönetici renk seçtiyse
  // onunki şemanın üzerine yazar.
  const color = fixed ? theme.accent : normalizeHexColor(primaryValue, theme.accent);
  // İkinci renk girilmediyse sabit mora dönmek yerine yöneticinin seçtiği
  // ana rengi kullan. Böylece tek renk seçimi üst bant dahil tüm temayı değiştirir.
  //
  // Tek istisna `keepSurface` işaretli şemalardır: kabuğun zemini bu renkten
  // türediği için (Zümrüt'ün zümrüt başlık/menü/footer gradyanları) ana renge
  // düşmek şemayı kendi kimliğinden çıkarırdı — orada şemanın kendi yüzey
  // rengine düşülür. İşaret bilinçli olarak şema başına verilir; işaretsiz
  // şemalarda davranış birebir eskisi gibidir.
  const surfaceFallback = theme.keepSurface && theme.surface ? theme.surface : color;
  const surfaceColor = fixed
    ? (theme.surface || color)
    : normalizeHexColor(surfaceValue, surfaceFallback);
  const rgb = toRgb(color);
  const surfaceRgb = toRgb(surfaceColor);
  const luminance = ((rgb[0] * 299) + (rgb[1] * 587) + (rgb[2] * 114)) / 1000;

  return {
    ...theme.tokens,
    '--pusu-gold': color,
    '--pusu-gold-rgb': rgb.join(', '),
    '--pusu-gold-2': toHex(mix(rgb, [0, 0, 0], 0.25)),
    '--pusu-gold-soft': `rgba(${rgb.join(', ')}, 0.12)`,
    '--pusu-accent-bright': toHex(mix(rgb, [255, 255, 255], 0.12)),
    '--pusu-accent-light': toHex(mix(rgb, [255, 255, 255], 0.3)),
    // Eşik 155 değil 140'tır: aradaki bantta kalan altın tonlarında
    // (ör. #ba8e47, ağırlıklı parlaklık 147) beyaz yazının kontrastı
    // 2.8:1'e düşüyor ve WCAG AA'yı geçmiyordu; siyah aynı zeminde
    // 7.5:1 veriyor. Mevcut şemaların vurgu renkleri bu bandın dışında
    // (78 / 78 / 159 / 181 / 190 / 232), yani ayar hiçbirini değiştirmez.
    '--pusu-on-accent': luminance > 140 ? '#111111' : '#ffffff',
    '--pusu-purple': surfaceColor,
    '--pusu-purple-rgb': surfaceRgb.join(', '),
    '--pusu-purple-2': toHex(mix(surfaceRgb, [255, 255, 255], 0.1)),
    '--pusu-purple-dark': toHex(mix(surfaceRgb, [0, 0, 0], 0.4)),
    '--pusu-purple-soft': `rgba(${surfaceRgb.join(', ')}, 0.45)`,
  };
};

// Android Chrome adres çubuğunu ve durum çubuğunu <meta name="theme-color">
// ile boyar. Değer sabit bırakılırsa (eskiden varsayılan şemanın kırmızısı)
// lacivert bir kurulumda üst şerit kırmızı açılır, oyuncu başka bir siteye
// düştüğünü sanar. Renk şemanın koyu zeminidir: çubuk sayfanın üstüyle aynı
// olur. index.html açılışta aynı değeri önbellekten yazar.
export const applyThemeColorMeta = (
  variables,
  doc = typeof document !== 'undefined' ? document : null,
) => {
  const color = variables['--pusu-bg-deep'] || variables['--pusu-bg'] || '';
  if (!doc || !color) return color;
  let meta = doc.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = doc.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    doc.head.appendChild(meta);
  }
  meta.setAttribute('content', color);
  return color;
};

export const applySiteTheme = (
  primaryValue,
  surfaceValue,
  themeId = DEFAULT_THEME_ID,
  root = typeof document !== 'undefined' ? document.documentElement : null,
) => {
  const variables = buildSiteTheme(primaryValue, surfaceValue, themeId);
  if (!root) return variables['--pusu-gold'];
  Object.entries(variables).forEach(([name, color]) => {
    root.style.setProperty(name, color);
  });
  // Değişkenle ifade edilemeyen şema dokunuşları bu seçiciye bağlıdır.
  root.setAttribute?.(SITE_THEME_ATTRIBUTE, normalizeThemeId(themeId));
  applyThemeColorMeta(variables);
  return variables['--pusu-gold'];
};

export const cacheSiteTheme = (
  primaryValue,
  surfaceValue,
  themeId = DEFAULT_THEME_ID,
  storage = typeof window !== 'undefined' ? window.localStorage : null,
) => {
  const variables = buildSiteTheme(primaryValue, surfaceValue, themeId);
  try {
    storage?.setItem(SITE_THEME_STORAGE_KEY, JSON.stringify(variables));
    storage?.setItem(SITE_THEME_ID_STORAGE_KEY, normalizeThemeId(themeId));
  } catch {
    // Gizli veya depolaması kapalı tarayıcılarda tema yine normal uygulanır.
  }
  return variables;
};
